import classNames from 'classnames';
import React, { useEffect, useMemo, useState } from 'react';
import {
	ColorKeys,
	CreateSession,
	ModuleMap,
	PageModuleType,
	Session,
	SessionTypesEnum,
	TextOverrides
} from '../../../../../../types/working-model';
import { AgendaLayoutTypesClassic, TemplateClassNames } from '../../../../../../types/template-layouts';
import { getStylingOverrides } from '../../../../../live-event/utils';
import TabHeader from '../../../../../general-ui/tab-header/tab-header';
import { TypographyItem } from '../../../../../general-ui/typography-item/typography-item';
import { getDefaultAgendaImage } from '../../../../../../store/utils/create-event';
import { Button } from '../../../../../general-ui/button/button';
import { getTextValue, updateTranslateKey } from '../../../../../../utils/utils';
import SessionCard from './agenda-session-card';
import { BroadcastSessions } from './broadcast-sessions';
import { OnDemandSessions } from './ondemand-sessions';
import Controls from '../../../session/editor/moduleControls/controls';
import TextInput from '../../../../../general-ui/text-input/text';
import { ICONS } from '../../../../../general-ui/icon';
import EditAgendaCard from './edit-agenda-card';
import { useDispatch, useSelector } from 'react-redux';
import { addNewSession, updateHomepageModule, updateSessionInfo, updateWorkingEvent } from '../../../../../../store/actions/admin/create-event';
import { EventsState } from '../../../../../../store/types';

import '../../../../../live-event/modules/agenda/agenda.scss';
import './agenda-editor.scss';
import SessionModal from '../../../session/session-modal';
import EditableTextSlim from '../../../../../general-ui/editable-text/editable-text-slim';
import { EContentColorOverrideNames } from '../../../session/editor/moduleControls/edit-section-colors-modal';
import EditableTextDraft from '../../../../../general-ui/editable-text/editable-text-draft';
import { useTranslation } from 'react-i18next';
import { BasicLangingModuleProps } from '../editor-main';

function filterBroadcasts(session: Session) {
	return session.session_type === SessionTypesEnum.broadcast;
}

function filterOnDemand(session: Session) {
	return session.session_type === SessionTypesEnum.onDemand;
}

const EditorAgenda: React.FC<BasicLangingModuleProps> = React.forwardRef<HTMLDivElement, BasicLangingModuleProps>((props, ref) => {
	const { page_module: module, template, language, baseLanguage } = props;
	const {
		CreateEventReducer: {
			workingEvent
		},
		AuthReducer: { token, user }
	} = useSelector((state: EventsState) => state);
	const { t } = useTranslation('homepage');
	const sessions = workingEvent?.sessions;
	const [activeTab, setActiveTab] = useState<string>();
	const [addingSession, setAddingSession] = useState<SessionTypesEnum | null>(null);
	const [searchSessions, setSearchSessions] = useState('');
	const [workingSessions, setWorkingSessions] = useState<Session[] | null>(null);
	const BROADCAST = t(`Broadcast sessions`);
	const ON_DEMAND = t(`On demand sessions`);
	//Because this module is mirroring its published counterpart it needs these variables.
	//We set them to false here so the admins will always be editing the unregistered, non - live version of agenda
	//Keeping the JSX code around in case the editor eventually allows you to see these versions
	const isUpNext = false;
	const isLive = false;

	const { content, styling_overrides } = module;
	const layout_type = content?.layout_type || AgendaLayoutTypesClassic.standard;

	const dispatch = useDispatch();


	//init as one variable so it can be passed to the card
	const stylingOverrides = getStylingOverrides(styling_overrides);
	const {
		backgroundColor,
		backgroundImg,
	} = stylingOverrides;

	const broadcastSessionList = useMemo(
		() => sessions?.filter(filterBroadcasts) || [],
		[sessions]
	);
	const onDemandSessionList = useMemo(
		() => sessions?.filter(filterOnDemand) || [],
		[sessions]
	);

	useEffect(() => {
		const interval = setTimeout(() => {
			setActiveTab(BROADCAST);
		});
		return () => clearTimeout(interval);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);


	const handleAddNewSession = () => {
		setAddingSession(SessionTypesEnum.onDemand);
	};
	function handleTextChange(key: string) {
		return (text: string) => {
			dispatch(
				updateHomepageModule({
					...module,
					content: {
						...content,
						[key]: updateTranslateKey({
							translateString: module?.content?.[key],
							input: text,
							language,
							baseLanguage
						}),
					},
				})
			);
		};
	}
	function handleStyleOverrideChange(key: string) {
		return (overrides: TextOverrides) => {
			const updatedOverrides = {
				...styling_overrides,
				content: {
					...styling_overrides?.content,
					[key]: overrides
				}
			};
			dispatch(
				updateHomepageModule({
					...module,
					styling_overrides: updatedOverrides
				})
			);
		};
	}
	const renderSessions = (): JSX.Element | undefined => {
		if (isLive) {
			return (
				<>
					<TabHeader
						tabs={[BROADCAST, ON_DEMAND]}
						onClick={setActiveTab}
						currentTab={activeTab}
						template={template}
					/>
					{activeTab === BROADCAST && broadcastSessionList.length && (
						<BroadcastSessions
							baseLanguage={baseLanguage}
							language={language}
							module={module}
							sessions={broadcastSessionList}
							template={template}
							isLive={isLive}
							isUpNext={isUpNext}
						/>
					)}
					{activeTab === ON_DEMAND && onDemandSessionList.length && (
						<OnDemandSessions
							baseLanguage={baseLanguage}
							language={language}
							module={module}
							sessions={onDemandSessionList}
							template={template}
							isLive={isLive}
						/>
					)}
				</>
			);
		} else {
			return <>
				{broadcastSessionList.length ? <BroadcastSessions
					baseLanguage={baseLanguage}
					language={language}
					module={module}
					sessions={broadcastSessionList}
					template={template}
					isLive={isLive}
					isUpNext={isUpNext}
				/> : null}
				{onDemandSessionList.length ? <OnDemandSessions
					baseLanguage={baseLanguage}
					language={language}
					module={module}
					sessions={onDemandSessionList}
					template={template}
					isLive={isLive}
				/> : null}
			</>;
		}
	};

	const visibleAgendas = useMemo(() => {
		if (workingEvent?.sessions) {
			const _sessions = workingSessions ? workingSessions.slice() : workingEvent?.sessions.slice();
			const _term = searchSessions.replace(/\s/g, '').trim().toLowerCase();
			return _sessions.filter(session => session.title.base.replace(/\s/g, '').toLowerCase().trim().includes(_term));
		} else {
			return [];
		}
	}, [searchSessions, workingEvent?.sessions, workingSessions]);

	function toggleVisibleSession(session: number) {
		if (workingEvent?.settings) {
			const _session = workingSessions ? workingSessions.find(s => s.session === session) : workingEvent.sessions.find(s => s.session === session);
			if (_session) {
				_session.is_on = !_session?.is_on;
				setWorkingSessions(workingSessions?.map(s => {
					if (s.session === session) {
						return _session;
					}
					return s;
				}) ?? null);
			}
		}
	}
	function removeVisibleSession(session: number) {
		if (workingEvent?.settings) {
			const _sessions = workingSessions ? workingSessions.slice() : workingEvent?.sessions.slice();
			const filteredSessions = _sessions.filter(s => s.session !== session);

			dispatch(updateWorkingEvent({
				...workingEvent!,
				sessions: filteredSessions
			}, token!));
		}
	}

	const handleCloseModal = () => setAddingSession(null);
	function createSession(session: CreateSession) {
		//if the user didn't pick a language, they get english
		if (session.languages.length === 0) {
			session.languages = ['en'];
			session.default_language = 'en';
		}

		//if there's no default language, make it english if it exists, otherwise pick the first non-english language
		if (!session.default_language) {
			if (session.languages.includes('en')) {
				session.default_language = 'en';
			} else {
				session.default_language = session.languages[0];
			}
		}

		dispatch(addNewSession(session, Number(workingEvent!.event), workingEvent!.uuid, user!.active_channel, token!));

		setAddingSession(null);
		return Promise.resolve();
	}

	const editAgenda = (
		<div className="edit-content">
			<div className="search">
				<TextInput
					onChange={(e) => setSearchSessions(e.target.value)}
					placeholder="Search sessions to add..."
					icon={ICONS.SEARCH}
					size={"small"}
				/>
				or
				<button className="button small">Create Session</button>
			</div>
			<div className="edit-agenda-cards">
				{
					visibleAgendas.map((session, idx) => {
						return (
							<EditAgendaCard
								key={session.session + idx}
								session={session}
								toggleDisplay={toggleVisibleSession}
								onEdit={() => ('placeholder')}
								onDelete={removeVisibleSession}
							/>
						);
					})
				}
			</div>
		</div>
	);

	const { custom_heading, title, description } = getTextValue(content, language);
	const customHeadingDefaultColor = (template === TemplateClassNames.O2)
		? ColorKeys.secondaryBackgroundColor
		: ColorKeys.secondaryAccentColor;

	if (!module.is_on) return null;
	return (
		<section
			ref={ref}
			id={ModuleMap[PageModuleType.agenda]}
			className={classNames(
				'editor-wrapper agenda agenda-editor',
				`agenda-${layout_type}`,
				backgroundColor,
				template
			)}
		>
			<Controls
				module={module}
				contentButtonText="Add session"
				openContentModal={handleAddNewSession}
				editableContent={{
					jsx: editAgenda,
					onDone: () => workingSessions ? workingSessions.map(s => {
						dispatch(updateSessionInfo(s, token!));
					}) : {},
				}}
				baseLanguage={baseLanguage}
				language={language}
			/>
			<div className="module-container" style={backgroundImg}>
				{
					!isUpNext ?
						<>
							<EditableTextSlim
								tagType="p"
								className="upper-agenda-title"
								value={custom_heading}
								defaultColor={customHeadingDefaultColor}
								onChange={handleTextChange('custom_heading')}
								onOverrideChange={handleStyleOverrideChange('custom_heading')}
								elementName={EContentColorOverrideNames.custom_heading}
								colorOverride={styling_overrides?.content?.custom_heading?.color}
							/>
							<EditableTextSlim
								tagType="h1"
								className="agenda-title"
								value={title}
								defaultColor={ColorKeys.bodyTextColor}
								onChange={handleTextChange('title')}
								onOverrideChange={handleStyleOverrideChange('title')}
								elementName={EContentColorOverrideNames.title}
								colorOverride={styling_overrides?.content?.title?.color}
							/>
							<EditableTextDraft
								className="agenda-description"
								value={description}
								onChange={handleTextChange("description")}
								defaultColor={ColorKeys.bodyTextColor}
								onOverrideChange={handleStyleOverrideChange('description')}
								elementName={EContentColorOverrideNames.description}
								colorOverride={styling_overrides?.content?.description?.color}
							/>
						</> :
						null
				}

				<div className={classNames('agenda-featured-container')}>
					{/* TODO: Figure out how we choose the "featured" session */}
					{
						sessions && sessions[0] && <SessionCard
							baseLanguage={baseLanguage}
							language={language}
							key={sessions[0].uuid}
							session={sessions[0]}
							stylingOverrides={stylingOverrides}
							template={template}
							defaultImage={getDefaultAgendaImage(template, 0)}
						/>
					}
				</div>
				<div className={classNames('agenda-sessions-container')}>
					{
						isUpNext &&
						<div className="agenda-next-sessions-header">
							<TypographyItem className={classNames('evt-heading-2 agenda-title')} tagName="h2">
								{t("Up next")}
							</TypographyItem>
							<Button
								tagName="a"
								url={'#'}
								classButton="tertiary"
								template={template}
							>
								{t("See agenda")}
							</Button>
						</div>
					}
					{renderSessions()}
				</div>
			</div>
			<SessionModal
				addingSession={!!addingSession}
				addingSessionType={addingSession || undefined}
				closeModal={handleCloseModal}
				createSession={createSession}
			/>
		</section>
	);
});

EditorAgenda.displayName = 'EditorAgenda';

export default EditorAgenda;
